import {
	SAVE_FAILED,
	SAVE_INVALID,
	SAVE_OK,
	SAVE_SKIPPED,
	useWizardStep,
} from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';
import type { WizardSaveResult } from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry.types';
import { updateInvestorData } from '@dealstack/frontend/shared/dataExchange/services/shareholder/investment.service';
import { getMainAddress } from '@dealstack/frontend/shared/address';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { investmentJoinAndAllocationContext } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/InvestmentJoinAndAllocation';
import { normalizePhoneNumberForBE } from '@dealstack/shared/lib/format/string';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { useCommonYupValidation } from '@dealstack/frontend/shared/other/react/form/useCommonYupValidation';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';
import { userNameUpdated } from '@dealstack/frontend/shared/mechanism/redux/reducers/user/user';
import { useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { getFormValuesFromInvestorData } from '@dealstack/frontend/investor/pages/Investment/common/form.helper.ts';

export enum JOIN_ALLOCATION_PERSONAL_INFO_FORM {
	FIRST_NAME = 'firstName',
	LAST_NAME = 'lastName',
	BIRTH_DATE = 'birthDate',
	COUNTRY = 'country',
	CITY = 'city',
	POST_CODE = 'postCode',
	STREET = 'street',
	HOUSE_NUMBER = 'houseNumber',
	TELEPHONE_PREFIX = 'telephonePrefix',
	PHONE_NUMBER = 'phoneNumber',
}

interface PersonalInfoForm {
	[JOIN_ALLOCATION_PERSONAL_INFO_FORM.FIRST_NAME]: string;
	[JOIN_ALLOCATION_PERSONAL_INFO_FORM.LAST_NAME]: string;
	[JOIN_ALLOCATION_PERSONAL_INFO_FORM.BIRTH_DATE]: string;
	[JOIN_ALLOCATION_PERSONAL_INFO_FORM.COUNTRY]: string;
	[JOIN_ALLOCATION_PERSONAL_INFO_FORM.CITY]: string;
	[JOIN_ALLOCATION_PERSONAL_INFO_FORM.POST_CODE]: string;
	[JOIN_ALLOCATION_PERSONAL_INFO_FORM.STREET]: string;
	[JOIN_ALLOCATION_PERSONAL_INFO_FORM.HOUSE_NUMBER]: string;
	[JOIN_ALLOCATION_PERSONAL_INFO_FORM.TELEPHONE_PREFIX]: string;
	[JOIN_ALLOCATION_PERSONAL_INFO_FORM.PHONE_NUMBER]: string;
}

export const useJoinAndAllocationPersonalInformationController = () => {
	const commonYup = useCommonYupValidation();
	const tInvestor = useTranslationInvestor();
	const dispatch = useDispatch();

	const updateInvestorDataRequest = useWrappedRequest(updateInvestorData);

	const { setIsLoading, setIsContinueDisabled, getInvestorDataRequest } = useContext(
		investmentJoinAndAllocationContext
	);

	const personalInformationForm = useForm<PersonalInfoForm>({
		mode: 'onChange',
		reValidateMode: 'onChange',
		resolver: yupResolver(
			Yup.object().shape({
				[JOIN_ALLOCATION_PERSONAL_INFO_FORM.FIRST_NAME]: commonYup.string.required,
				[JOIN_ALLOCATION_PERSONAL_INFO_FORM.LAST_NAME]: commonYup.string.required,
				[JOIN_ALLOCATION_PERSONAL_INFO_FORM.BIRTH_DATE]: commonYup.date.past.required,
				[JOIN_ALLOCATION_PERSONAL_INFO_FORM.COUNTRY]: commonYup.string.required,
				[JOIN_ALLOCATION_PERSONAL_INFO_FORM.CITY]: commonYup.string.required,
				[JOIN_ALLOCATION_PERSONAL_INFO_FORM.POST_CODE]: commonYup.string.required,
				[JOIN_ALLOCATION_PERSONAL_INFO_FORM.STREET]: commonYup.string.required,
				[JOIN_ALLOCATION_PERSONAL_INFO_FORM.HOUSE_NUMBER]: commonYup.string.required,
				[JOIN_ALLOCATION_PERSONAL_INFO_FORM.TELEPHONE_PREFIX]: commonYup.string.required,
				[JOIN_ALLOCATION_PERSONAL_INFO_FORM.PHONE_NUMBER]: commonYup.phone.required,
			})
		),
	});

	useEffect(() => {
		const payload = getInvestorDataRequest?.response?.payload;
		if (!payload) {
			return;
		}

		const formValues = getFormValuesFromInvestorData(
			payload,
			JOIN_ALLOCATION_PERSONAL_INFO_FORM
		);

		// Allow e2e tests to pre-fill the birth date via URL search param,
		// bypassing the unreliable Mantine DateInput interaction
		const birthDateParam = new URLSearchParams(window.location.search).get('birthDate');
		if (birthDateParam) {
			formValues[JOIN_ALLOCATION_PERSONAL_INFO_FORM.BIRTH_DATE] = birthDateParam;
		}

		personalInformationForm.reset(formValues, { keepDirtyValues: true });
		void personalInformationForm.trigger().then(() => personalInformationForm.clearErrors());
	}, [getInvestorDataRequest?.response?.payload]);

	const { isValid } = personalInformationForm.formState;

	useEffect(() => {
		setIsContinueDisabled(!isValid);

		return () => {
			setIsContinueDisabled(false);
		};
	}, [isValid]);

	const onSubmit = (formValues: PersonalInfoForm): Promise<WizardSaveResult> =>
		new Promise((resolve) => {
			setIsLoading(true);

			const phoneNumber = normalizePhoneNumberForBE(
				formValues[JOIN_ALLOCATION_PERSONAL_INFO_FORM.PHONE_NUMBER],
				formValues[JOIN_ALLOCATION_PERSONAL_INFO_FORM.TELEPHONE_PREFIX]
			);

			const formFirstName = formValues[JOIN_ALLOCATION_PERSONAL_INFO_FORM.FIRST_NAME];
			const formLastName = formValues[JOIN_ALLOCATION_PERSONAL_INFO_FORM.LAST_NAME];

			updateInvestorDataRequest
				.fetch({
					firstName: formFirstName,
					lastName: formLastName,
					birthDate: formValues[JOIN_ALLOCATION_PERSONAL_INFO_FORM.BIRTH_DATE],
					addressId: getMainAddress(
						getInvestorDataRequest?.response?.payload?.person?.addresses
					)?.id,
					country: formValues[JOIN_ALLOCATION_PERSONAL_INFO_FORM.COUNTRY],
					city: formValues[JOIN_ALLOCATION_PERSONAL_INFO_FORM.CITY],
					postCode: formValues[JOIN_ALLOCATION_PERSONAL_INFO_FORM.POST_CODE],
					street: formValues[JOIN_ALLOCATION_PERSONAL_INFO_FORM.STREET],
					houseNumber: formValues[JOIN_ALLOCATION_PERSONAL_INFO_FORM.HOUSE_NUMBER],
					telephonePrefix:
						formValues[JOIN_ALLOCATION_PERSONAL_INFO_FORM.TELEPHONE_PREFIX],
					phoneNumber,
				})
				.onAnyError(() => {
					resolve(SAVE_FAILED);
					setIsLoading(false);
				})
				.onLocalError(() => {
					showToastV2({
						title: tInvestor('investment.toast.failed_save_personal_information'),
						state: 'error',
					});
				})
				.onSuccess(async () => {
					await getInvestorDataRequest.fetch().concludeWithPromise();
					showToastV2({ title: tInvestor('investment.toast.data_saved') });

					const isNameChanged =
						formFirstName !==
							getInvestorDataRequest?.response?.payload?.user?.firstName ||
						formLastName !== getInvestorDataRequest?.response?.payload?.user?.lastName;

					if (isNameChanged) {
						dispatch(
							userNameUpdated({
								firstName: formFirstName,
								lastName: formLastName,
							})
						);
					}

					resolve(SAVE_OK);
					setIsLoading(false);
				});
		});

	useWizardStep(async (intent) => {
		if (intent !== 'next') {
			return SAVE_SKIPPED;
		}
		const isValid = await personalInformationForm.trigger();
		if (!isValid) {
			return SAVE_INVALID;
		}
		return onSubmit(personalInformationForm.getValues());
	});

	return {
		personalInformationForm,
		onSubmit,
	};
};
